import { Component, computed, input } from '@angular/core';
import { OrganizationChartModule } from 'primeng/organizationchart';
import { TreeNode } from 'primeng/api';

import { OrgNode } from './dashboard-widget.model';
import { CardHeaderComponent } from './card-header.component';
import { initialsOf } from '../../../shared/utils/employee-name.util';

@Component({
  selector: 'app-org-tile',
  standalone: true,
  imports: [OrganizationChartModule, CardHeaderComponent],
  template: `
    <div
      class="card flex h-full flex-col gap-3 rounded-xl border border-surface-200/60 bg-surface-0 p-5 dark:border-surface-700/40 dark:bg-surface-900"
    >
      <app-card-header [title]="title()" [subtitle]="subtitle()" [icon]="icon()" [accent]="accent()" />

      @if (loading()) {
      <div class="h-40 w-full animate-pulse rounded-lg bg-surface-100 dark:bg-surface-800"></div>
      } @else if (nodes().length) {
      <div class="overflow-x-auto pb-1">
        <p-organizationChart [value]="nodes()" [preserveSpace]="false" styleClass="dashboard-reporting">
          <ng-template let-node pTemplate="person">
            <div
              class="flex min-w-36 flex-col items-center gap-2 rounded-lg border px-4 py-3"
              [class]="
                node.data.self
                  ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-500/10'
                  : 'border-surface-200/70 bg-surface-0 dark:border-surface-700/50 dark:bg-surface-900'
              "
            >
              <!-- Avatar, with an initials circle as the fallback. -->
              @if (node.data.avatar) {
              <img
                [src]="node.data.avatar"
                [alt]="node.data.name"
                class="h-10 w-10 rounded-full object-cover"
                (error)="node.data.avatar = undefined"
              />
              } @else {
              <span
                class="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold"
                [class]="
                  node.data.self
                    ? 'bg-primary-500 text-white dark:bg-primary-400'
                    : 'bg-surface-100 text-text-muted dark:bg-surface-800'
                "
                >{{ initials(node.data.name) }}</span
              >
              }
              <div class="flex flex-col items-center">
                <span
                  class="text-sm font-semibold"
                  [class]="node.data.self ? 'text-primary-700 dark:text-primary-300' : 'text-text-main'"
                  >{{ node.data.name }}</span
                >
                @if (node.data.title) {
                <span class="text-xs text-text-muted">{{ node.data.title }}</span>
                }
              </div>
            </div>
          </ng-template>
        </p-organizationChart>
      </div>
      } @else {
      <div class="flex h-40 items-center justify-center text-sm text-text-muted">—</div>
      }
    </div>
  `,
})
export class OrgTileComponent {
  title = input.required<string>();
  subtitle = input<string>('');
  icon = input<string>('');
  accent = input<string>('');
  org = input<OrgNode | undefined>(undefined);
  loading = input<boolean>(false);

  /** Map our OrgNode tree to PrimeNG TreeNodes, expanded, tagged 'person'. */
  nodes = computed<TreeNode[]>(() => {
    const root = this.org();
    return root ? [this.toNode(root)] : [];
  });

  private toNode(n: OrgNode): TreeNode {
    return {
      label: n.name,
      type: 'person',
      expanded: true,
      data: n,
      children: (n.children ?? []).map((c) => this.toNode(c)),
    };
  }

  readonly initials = initialsOf;
}
